import React, { useState, useEffect } from 'react';
import OrderService from '../services/OrderService';

import dine_in from '../images/dine-in.svg';
import delivery from '../images/delivery.svg';
import takeway from '../images/take-away.svg';
import ongoing_food from '../images/ongoing-food.svg';

import served from '../images/food-served.svg';
import pickedup from '../images/food-picked.svg';
import dispatch from '../images/food-dispatch.svg';

import table from '../images/table.svg';
import bill from '../images/bill.svg';

import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';

<style>
    {`
  @keyframes zoomPulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.15); }
    100% { transform: scale(1); }
  }
`}
</style>

const ServedOrderCard = ({ order }) => {
    // For Served orders, we do NOT use a running timer. 
    // We calculate the static delay based on completedAt - targetCompletionTime.

    // We expect order.completedAt to be available from the backend.
    // If not available (e.g. during transition), we might fallback or show "N/A".

    // Logic:
    // delaySeconds = completedAt - targetCompletionTime
    // If delaySeconds > 0: "Delayed" and show delay duration.
    // If delaySeconds <= 0: "On Time" (or "Early"). Show duration? Or just "On Time".
    // User request: "show if the order was delayed or not and by how much time"

    const calculateDelay = () => {
        if (!order.completedAt || !order.targetCompletionTime) return { status: 'UNKNOWN', duration: 0 };

        const completed = new Date(order.completedAt);
        const target = new Date(order.targetCompletionTime);
        const diffInSeconds = Math.floor((completed - target) / 1000);

        return {
            status: diffInSeconds > 0 ? 'DELAYED' : 'ON_TIME',
            duration: Math.abs(diffInSeconds)
        };
    };

    const { status, duration } = calculateDelay();

    const formatDuration = (seconds) => {
        const absSeconds = Math.abs(seconds);
        const mins = Math.floor(absSeconds / 60).toString().padStart(2, '0');
        const secs = (absSeconds % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    };

    const getStatusTag = () => {
        if (status === 'DELAYED') {
            // For delayed orders, we might want to differentiate slight vs long delay if needed, 
            // but user asked "delayed or not".
            // Using logic similar to Landing for coloring consistency if we want.
            if (duration <= 60) return { text: 'Delay', className: 'delay_tag' }; // Or slight_delay_tag? 
            // Landing: <= 60 is Delay (Red), 60-120 is Slight Delay (Orange). 
            // Logic in Landing for POSITIVE timeLeft (not overdue) is:
            // <= 60 (near deadline): Red/Delay
            // <= 120: Orange/Slight Delay
            // 
            // For SERVED orders, a Positive duration means OVERDUE (Late).
            // So if duration > 0, it WAS late.
            // Let's use 'Late' or 'Delayed'.
            return { text: 'Delayed', className: 'delay_tag' };
        } else if (status === 'ON_TIME') {
            return { text: 'On Time', className: 'on_time_tag' };
        } else {
            return { text: '--', className: '' };
        }
    };

    const getTextStyle = () => {
        if (status === 'DELAYED') {
            return { color: '#D22633' }; // Red for delayed
        } else {
            return { color: '#000000' };
        }
    }

    const timeStatus = getStatusTag();

    const getOrderTypeIcon = (type) => {
        switch (type) {
            case 'DINE_IN': return dine_in;
            case 'DELIVERY': return delivery;
            case 'TAKE_AWAY': return takeway;
            default: return dine_in;
        }
    };

    const getStatusInfo = (status) => {
        switch (status) {
            case 'SERVED': return { icon: served, text: 'SERVED' };
            case 'PICKED_UP': return { icon: pickedup, text: 'PICKED UP' }; // Note space in text vs underscore in ENUM
            case 'DISPATCHED': return { icon: dispatch, text: 'DISPATCHED' };
            default: return { icon: served, text: status };
        }
    };

    const statusInfo = getStatusInfo(order.status);

    return (
        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-3 col-sm-12 col-12">
            <div className="white_bg_color rounded mb-4 item_box">

                {/* date time and food delivery */}
                <div className="border-bottom d-flex justify-content-between align-items-center p-3">
                    <div className="mb-0">
                        <p className='ex_small_text mb-0'>{new Date(order.placedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase().replace(',', ' |')}</p>
                    </div>

                    <div className="mb-0">
                        <p className='small_text mb-0'>{order.orderType?.replace('_', '-')} <img src={getOrderTypeIcon(order.orderType)} alt='' className='ms-2' style={{ width: '35px' }}></img></p>
                    </div>
                </div>
                {/* date time and food delivery */}

                <div className="clearfix"></div>

                {/* table and bill no */}
                <div className="border-bottom d-flex justify-content-between align-items-center no_box served_box" style={{ backgroundColor: '#EAEAEA' }}>

                    <div className="col px-3 py-2 d-flex align-items-center border_end">
                        <div className="text-center me-2">
                            <img src={table} alt='' style={{ width: '40px' }}></img>
                            <p className='ex_small_text mb-0'>&nbsp;</p>
                        </div>

                        <div className="div">
                            <p className='orange_text mb-0'>Table No</p>
                            <h1 className='heading fw-bold mb-0'>{order.tableNo || '--'}</h1>
                        </div>
                    </div>

                    <div className="col px-3 py-2 d-flex align-items-center">
                        <div className="text-center me-2">
                            <img src={bill} alt='' className='me-2' style={{ width: '40px' }}></img>
                        </div>

                        <div className="div">
                            <p className='orange_text mb-0'>Bill No</p>
                            <h1 className='heading fw-bold mb-0'>{order.billNo}</h1>
                        </div>

                    </div>
                </div>
                {/* table and bill no */}

                <div className="clearfix"></div>

                {/* order status */}
                <div className="border-bottom d-flex justify-content-between align-items-center item_status_box">

                    <div className="col p-3 d-flex align-items-center border-end">
                        <div className="text-center me-2">
                            <img src={statusInfo.icon} alt='' style={{ width: '70px', height: '70px' }}></img>
                        </div>

                        <div className="div">
                            <p className='served_tag mb-0'>{statusInfo.text}</p>
                        </div>
                    </div>

                    <div className="col p-3 d-flex align-items-center text-center">

                        <div className="w-100">
                            <h1 className='heading fw-bold mb-0 roboto_consended'>100%</h1>
                            <div className="progress" style={{ height: '8px' }}>
                                <div className="progress-bar bg_green" role="progressbar" style={{ width: '100%', height: '8px' }} aria-valuenow="100" aria-valuemin="0" aria-valuemax="100"></div>
                            </div>
                        </div>

                    </div>
                </div>
                {/* order status */}

                <div className="clearfix"></div>

                {/* order timing */}
                <div className="d-flex justify-content-between align-items-center order_timer_box">

                    <div className="col p-3 d-flex align-items-center">
                        &nbsp;
                    </div>

                    <div className="col p-3 d-flex align-items-center timing_box">
                        <div className="text-center me-2 border-end pe-2">
                            {/* Using timeStatus logic from Landing for consistency */}
                            <p className={`mb-0 ${timeStatus.className}`}>{timeStatus.text}</p>
                            <h1 className='sub_heading fw-bold mb-0' style={{ fontSize: '25px' }}>10:00</h1>
                        </div>

                        <div className="div">
                            <h1 className='heading fw-bold mb-0' style={getTextStyle()}>{formatDuration(duration)}</h1>
                        </div>

                    </div>
                </div>
                {/* order timing */}

            </div>
        </div>
    )
}

export default function Served() {
    const [selectedOption, setSelectedOption] = useState('All');
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const data = await OrderService.getOrders('served', selectedOption);
            setOrders(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        const interval = setInterval(fetchOrders, 30000); // Polling every 30s
        return () => clearInterval(interval);
    }, [selectedOption]);


    const handleSelect = (option) => {
        setSelectedOption(option);
    };

    return (
        <div className="inner_body">
            <PageHeader />

            <div className="container-fluid">
                <div className="row m-auto">
                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-12">
                        <h1 className="sub_heading">Orders | Served</h1>
                    </div>

                    {/* filter dowpdown */}
                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12 col-12 text-end">
                        <div className="dropdown">
                            <button className="btn btn-light dropdown-toggle text-start border" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false" style={{ width: '180px' }}>
                                <i className="fa-solid fa-sliders"></i> &nbsp; {selectedOption}
                            </button>

                            <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                {['All', 'Dine-In', 'Delivery', 'Take Away'].map((option) => (
                                    <li key={option}>
                                        <button className="dropdown-item" onClick={() => handleSelect(option)}>
                                            {option}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    {/* filter dowpdown */}

                    <div className="clearfix"></div>
                </div>
            </div>

            {/* ================== */}
            {/* ================== */}

            {/* card section */}
            <div className="container-fluid mt-3 mb-5">
                <div className="row m-auto">
                    {loading && <p>Loading orders...</p>}
                    {!loading && orders.length === 0 && <p>No served orders found.</p>}

                    {orders.map(order => (
                        <ServedOrderCard key={order.id} order={order} />
                    ))}
                </div>
            </div>
            {/* card section */}

            {/* floating btn */}
            <a href='/'>
                <div className="floating_btn ongoing_food">
                    <div className="d-flex justify-content-center align-items-center flex-wrap">
                        <img src={ongoing_food} alt='' style={{ width: '40px' }}></img>
                        <p className='fw-bold mb-0'>ONGOING</p>
                    </div>
                </div>
            </a>

            {/* floating btn */}

            <Footer />
        </div>
    );
}
